import React, { useState, useMemo, useEffect } from 'react';
import { WebCryptoRamManager } from './crypto/webcrypto-ram';
import { PairingGenerator } from './components/PairingGenerator';
import { InterventionStudio } from './components/InterventionStudio';
import { WebTargetStudio } from './components/WebTargetStudio';
import { StrictnessTierScheduler } from './components/StrictnessTierScheduler';
import { DecryptedAuditViewer, AuditEnvelopeItem } from './components/DecryptedAuditViewer';
import { PatientRosterView } from './components/PatientRosterView';
import { OrganizationStudioView } from './components/OrganizationStudioView';
import { MarketingLandingPage } from './components/MarketingLandingPage';
import { ClinicianAuthModal, ClinicianProfile } from './components/ClinicianAuthModal';
import { LuminousBreakLogo } from './components/LuminousBreakLogo';
import { StripeCustomerPortalModal } from './components/StripeCustomerPortalModal';
import { IndividualDashboardView } from './components/IndividualDashboardView';
import { useInactivityLock } from './hooks/useInactivityLock';
import { InactivityLockOverlay } from './components/InactivityLockOverlay';
import { ComplianceCenterModal } from './components/ComplianceCenterModal';
import { PrivacyPolicyPage } from './components/PrivacyPolicyPage';
import { TermsOfServicePage } from './components/TermsOfServicePage';
import { SupportContactPage } from './components/SupportContactPage';

export type AppRoute = 'landing' | 'portal' | 'individual' | 'privacy' | 'terms' | 'support';

export const getInitialRoute = (): AppRoute => {
  if (typeof window === 'undefined') return 'landing';
  const path = window.location.pathname.toLowerCase().replace(/\/+$/, '');
  if (path === '/privacy') return 'privacy';
  if (path === '/terms') return 'terms';
  if (path === '/support') return 'support';
  return 'landing';
};

export const App: React.FC = () => {
  const cryptoManager = useMemo(() => new WebCryptoRamManager(), []);
  // Core view state: [currentView, setCurrentView] = useState<'landing' | 'portal' | 'individual'>('landing');
  const [currentView, setCurrentView] = useState<AppRoute>(getInitialRoute);
  const [activeTab, setActiveTab] = useState<'targets' | 'roster' | 'organization' | 'pairing' | 'studio' | 'scheduler' | 'audit'>('targets');
  const [isAuthModalOpen, setIsAuthModalOpen] = useState<boolean>(false);
  const [isCustomerPortalOpen, setIsCustomerPortalOpen] = useState<boolean>(false);
  const [isComplianceModalOpen, setIsComplianceModalOpen] = useState<boolean>(false);

  const { isLocked, unlock, lockNow } = useInactivityLock({
    timeoutMs: 15 * 60 * 1000,
    onZeroize: () => {
      cryptoManager.zeroizeKeys();
      setMockEnvelopes([]);
    },
  });
  const [currentPractitioner, setCurrentPractitioner] = useState<ClinicianProfile>({
    name: 'Care Lead Practitioner',
    clinic: 'Clinical Care Center',
    email: 'practitioner@luminousbreak.com',
    role: 'Clinical Director',
  });
  const [mockEnvelopes, setMockEnvelopes] = useState<AuditEnvelopeItem[]>([]);

  const seedSampleEnvelopes = async () => {
    const keys = cryptoManager.getActiveKeyPair() || (await cryptoManager.generateKeyPair());
    const payload1 = {
      action: 'protection_target_toggled',
      category: 'financial_speculation',
      target: 'binance.com',
      enabled: true,
      modifiedBy: 'user',
    };
    const enc1 = await cryptoManager.encryptPayload(payload1, keys.publicKey, 'full_metadata');

    const payload2 = {
      action: 'strictness_mode_changed',
      category: 'mindful_media',
      oldTier: 'gentle_nudge',
      newTier: 'friction_delay',
      modifiedBy: 'user',
    };
    const enc2 = await cryptoManager.encryptPayload(payload2, keys.publicKey, 'local_slm_summary_only');

    const sampleItems: AuditEnvelopeItem[] = [
      {
        eventId: 'evt_sample_001',
        userId: 'client_usr_882',
        ciphertextBase64: enc1.ciphertextBase64,
        ivBase64: enc1.ivBase64,
        tagBase64: enc1.tagBase64,
        wrappedKeyBase64: enc1.wrappedKeyBase64,
        aclMode: 'full_metadata',
        timestamp: Date.now() - 1000 * 60 * 12,
      },
      {
        eventId: 'evt_sample_002',
        userId: 'client_usr_882',
        ciphertextBase64: enc2.ciphertextBase64,
        ivBase64: enc2.ivBase64,
        tagBase64: enc2.tagBase64,
        wrappedKeyBase64: enc2.wrappedKeyBase64,
        aclMode: 'local_slm_summary_only',
        timestamp: Date.now() - 1000 * 60 * 3,
      },
    ];

    setMockEnvelopes(sampleItems);
  };

  useEffect(() => {
    const onPopState = () => setCurrentView(getInitialRoute());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const navigateTo = (route: AppRoute) => {
    setCurrentView(route);
    if (typeof window !== 'undefined') {
      const targetPath = route === 'landing' ? '/' : `/${route}`;
      window.history.pushState({}, '', targetPath);
    }
  };

  if (currentView === 'privacy') {
    return (
      <PrivacyPolicyPage
        onNavigateHome={() => navigateTo('landing')}
        onNavigateTerms={() => navigateTo('terms')}
        onNavigateSupport={() => navigateTo('support')}
      />
    );
  }

  if (currentView === 'terms') {
    return (
      <TermsOfServicePage
        onNavigateHome={() => navigateTo('landing')}
        onNavigatePrivacy={() => navigateTo('privacy')}
        onNavigateSupport={() => navigateTo('support')}
      />
    );
  }

  if (currentView === 'support') {
    return (
      <SupportContactPage
        onNavigateHome={() => navigateTo('landing')}
        onNavigatePrivacy={() => navigateTo('privacy')}
        onNavigateTerms={() => navigateTo('terms')}
      />
    );
  }

  if (currentView === 'landing') {
    return (
      <>
        <MarketingLandingPage
          onOpenPortal={() => setIsAuthModalOpen(true)}
          onOpenBillingPortal={() => setIsCustomerPortalOpen(true)}
          onNavigateRoute={navigateTo}
        />
        <ClinicianAuthModal
          isOpen={isAuthModalOpen}
          onClose={() => setIsAuthModalOpen(false)}
          onLoginSuccess={(practitioner) => {
            setCurrentPractitioner(practitioner);
            setCurrentView('portal');
          }}
        />
        <StripeCustomerPortalModal
          isOpen={isCustomerPortalOpen}
          onClose={() => setIsCustomerPortalOpen(false)}
        />
        <ComplianceCenterModal
          isOpen={isComplianceModalOpen}
          onClose={() => setIsComplianceModalOpen(false)}
          cryptoManager={cryptoManager}
          onTriggerZeroize={() => {
            cryptoManager.zeroizeKeys();
            setMockEnvelopes([]);
          }}
          onTriggerLock={lockNow}
        />
        <InactivityLockOverlay
          isLocked={isLocked}
          onUnlock={unlock}
        />
      </>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#FAF8F5',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      color: '#1E293B',
    }}>
      {/* Top Practitioner & Subscriber Navigation Bar */}
      <header style={{
        backgroundColor: '#FFFFFF',
        borderBottom: '1px solid #E2E8F0',
        padding: '16px 32px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '14px',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <LuminousBreakLogo size={42} />
          <div>
            <h1 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#2D2826', letterSpacing: '-0.3px' }}>
              Luminous Break
            </h1>
            <span style={{ fontSize: '12px', color: '#64748B', fontWeight: 500 }}>
              {currentView === 'portal'
                ? `${currentPractitioner.name} • ${currentPractitioner.clinic}`
                : 'Individual Subscriber Workspace • Zero-Knowledge'}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          {/* View Switcher: Clinician vs Individual */}
          <div
            role="group"
            aria-label="Workspace View Switcher"
            style={{
              display: 'flex',
              backgroundColor: '#F1F5F9',
              padding: '3px',
              borderRadius: '8px',
              border: '1px solid #E2E8F0',
            }}
          >
            <button
              onClick={() => setCurrentView('portal')}
              aria-pressed={currentView === 'portal'}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: currentView === 'portal' ? '#5E8276' : 'transparent',
                color: currentView === 'portal' ? '#FFFFFF' : '#64748B',
                fontWeight: 700,
                fontSize: '12px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.15s ease',
              }}
            >
              👩‍⚕️ Clinician View
            </button>
            <button
              onClick={() => setCurrentView('individual')}
              aria-pressed={currentView === 'individual'}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: currentView === 'individual' ? '#5E8276' : 'transparent',
                color: currentView === 'individual' ? '#FFFFFF' : '#64748B',
                fontWeight: 700,
                fontSize: '12px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.15s ease',
              }}
            >
              👤 Individual Dashboard
            </button>
          </div>

          <button
            onClick={() => setIsCustomerPortalOpen(true)}
            aria-label="Individual Account & Billing"
            style={{
              backgroundColor: '#FAF8F5',
              border: '1px solid #D1C7BD',
              borderRadius: '8px',
              padding: '7px 14px',
              fontSize: '12px',
              fontWeight: 700,
              color: '#2D2826',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            💳 Individual Account & Billing
          </button>

          <button
            onClick={() => setIsComplianceModalOpen(true)}
            aria-label="Compliance & Privacy"
            style={{
              backgroundColor: '#FAF8F5',
              border: '1px solid #D1C7BD',
              borderRadius: '8px',
              padding: '7px 14px',
              fontSize: '12px',
              fontWeight: 700,
              color: '#2D2826',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            🛡️ Compliance & Privacy
          </button>

          <button
            onClick={() => setCurrentView('landing')}
            style={{
              backgroundColor: '#FAF8F5',
              border: '1px solid #E2E8F0',
              borderRadius: '8px',
              padding: '7px 14px',
              fontSize: '12px',
              fontWeight: 600,
              color: '#475569',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            🌐 Public Website
          </button>

          {currentView === 'portal' && (
            <button
              onClick={seedSampleEnvelopes}
              style={{
                backgroundColor: '#F1F5F9',
                border: '1px solid #CBD5E1',
                borderRadius: '8px',
                padding: '7px 14px',
                fontSize: '12px',
                fontWeight: 600,
                color: '#475569',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              🌱 Seed 4 Mock Patients
            </button>
          )}

          <button
            onClick={() => setCurrentView('landing')}
            style={{
              backgroundColor: '#FEE2E2',
              color: '#DC2626',
              border: 'none',
              borderRadius: '8px',
              padding: '7px 14px',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            Sign Out
          </button>
        </div>
      </header>

      {/* Main Workspace Container */}
      <main style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
        {currentView === 'individual' ? (
          <IndividualDashboardView
            cryptoManager={cryptoManager}
            onOpenStripePortal={() => setIsCustomerPortalOpen(true)}
            onNavigateHome={() => setCurrentView('landing')}
          />
        ) : (
          <>
            {/* Clinician Navigation Tabs */}
            <div
              className="coach-portal-tabs-container"
              role="tablist"
              aria-label="Coach Portal Navigation Tabs"
              style={{
                display: 'flex',
                gap: '8px',
                backgroundColor: '#FFFFFF',
                padding: '6px',
                borderRadius: '14px',
                marginBottom: '24px',
                border: '1px solid #E2E8F0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'thin',
              }}
            >
              {[
                { id: 'targets', label: '🎯 Target Configuration Studio' },
                { id: 'roster', label: '👥 Patient & Client Roster' },
                { id: 'organization', label: '🏢 Clinic & Team' },
                { id: 'pairing', label: '🤝 Client Pairing' },
                { id: 'studio', label: '🎨 Intervention Studio' },
                { id: 'scheduler', label: '⏰ Strictness Schedules' },
                { id: 'audit', label: '🛡️ Decrypted Audit Stream' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  style={{
                    flex: '1 0 auto',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    padding: '12px 16px',
                    borderRadius: '10px',
                    border: 'none',
                    backgroundColor: activeTab === tab.id ? '#5E8276' : 'transparent',
                    color: activeTab === tab.id ? '#FFFFFF' : '#64748B',
                    fontWeight: 700,
                    fontSize: '13px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content Views */}
            {activeTab === 'targets' && (
              <WebTargetStudio userId="client_usr_882" deviceId="web_portal_coach" userRole="clinician" />
            )}

            {activeTab === 'roster' && (
              <PatientRosterView
                cryptoManager={cryptoManager}
                coachId={currentPractitioner.email}
                coachName={currentPractitioner.name}
              />
            )}

            {activeTab === 'organization' && (
              <OrganizationStudioView />
            )}

            {activeTab === 'pairing' && (
              <PairingGenerator
                coachName={currentPractitioner.name}
                clinicName={currentPractitioner.clinic}
                cryptoManager={cryptoManager}
              />
            )}

            {activeTab === 'studio' && (
              <InterventionStudio
                coachId={currentPractitioner.email}
                coachName={currentPractitioner.name}
              />
            )}

            {activeTab === 'scheduler' && (
              <StrictnessTierScheduler />
            )}

            {activeTab === 'audit' && (
              <DecryptedAuditViewer cryptoManager={cryptoManager} envelopes={mockEnvelopes} />
            )}
          </>
        )}
      </main>

      <StripeCustomerPortalModal
        isOpen={isCustomerPortalOpen}
        onClose={() => setIsCustomerPortalOpen(false)}
      />

      <ComplianceCenterModal
        isOpen={isComplianceModalOpen}
        onClose={() => setIsComplianceModalOpen(false)}
        cryptoManager={cryptoManager}
        onTriggerZeroize={() => {
          cryptoManager.zeroizeKeys();
          setMockEnvelopes([]);
        }}
        onTriggerLock={lockNow}
      />

      <InactivityLockOverlay
        isLocked={isLocked}
        onUnlock={unlock}
      />
    </div>
  );
};

export default App;

